
import React, { useState } from 'react';
import MRRMovementChart from './reports/MRRMovementChart';
import CohortHeatmap from './reports/CohortHeatmap';
import UnitEconomicsCard from './reports/UnitEconomicsCard';
import ChurnAnalyticsChart from './reports/ChurnAnalyticsChart';
import RevenueByPlanChart from './reports/RevenueByPlanChart';
import TrialFunnel from './reports/TrialFunnel';
import ARPUTrendChart from './reports/ARPUTrendChart';
import NRRTrendChart from './reports/NRRTrendChart';
import EngagementStickinessChart from './reports/EngagementStickinessChart';
import GeographicRevenueChart from './reports/GeographicRevenueChart';
import SubscriptionLifecycleReport from './reports/SubscriptionLifecycleReport';
import ExecutiveGrowthReport from './reports/ExecutiveGrowthReport';
import CreateReportModal from './reports/CreateReportModal';
import GSTR1Report from './reports/GSTR1Report';
import ReportsHeader from './reports/ReportsHeader';
import { DateRangeOption } from './reports/ReportDateSelector';
import { mockMRRMovement } from '../services/mockData';
import { jsonToCSV, downloadFile } from '../services/exportService';
import { api } from '../services/api';
import { useSettings } from './SettingsContext';
import { logActivity } from '../utils/activityTracker';

const Reports: React.FC = () => {
  const { settings } = useSettings();
  const [userId, setUserId] = useState<string | null>(null);
  const [isCreatingReport, setIsCreatingReport] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState({ title: '', sub: '' });
  const [isExporting, setIsExporting] = useState(false);
  const [globalRange, setGlobalRange] = useState<DateRangeOption>('30d');

  React.useEffect(() => {
    const fetchUser = async () => {
      if (!api.auth.isAuthenticated()) return;
      try {
        const user = await api.auth.me();
        setUserId(user.id);
      } catch (e) {
        if (e instanceof Error && e.message !== 'Unauthorized') {
          console.error("Failed to fetch user", e);
        }
      }
    };
    fetchUser();
  }, []);

  const handleCreateReport = async (name: string) => {
    if (userId) {
      await logActivity(userId, 'create_report', { reportName: name });
    }
    setToastMessage({ title: 'Report Generated', sub: `"${name}" is now available.` });
    setIsCreatingReport(false);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  };

  const handleExportCSV = async () => {
    setIsExporting(true);
    try {
      const csvData = jsonToCSV(mockMRRMovement);
      downloadFile(csvData, `subscale-report-${new Date().toISOString().split('T')[0]}.csv`, 'text/csv;');
      
      if (userId) {
        await logActivity(userId, 'export_report', { reportType: 'csv' });
      }
      
      setToastMessage({ title: 'Export Successful', sub: 'CSV report downloaded.' });
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-10 animate-in fade-in duration-700 pb-20 relative">
      <ReportsHeader 
        range={globalRange} 
        setRange={setGlobalRange}
        onBuildReport={() => setIsCreatingReport(true)}
        onExport={handleExportCSV}
        isExporting={isExporting}
      />

      {showToast && (
        <div className="fixed bottom-8 right-8 z-[100] animate-in slide-in-from-bottom-4">
          <div className="bg-slate-900 text-white px-6 py-4 rounded-2xl shadow-2xl flex items-center gap-4 border border-white/10">
            <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
            <div><p className="text-sm font-black">{toastMessage.title}</p><p className="text-xs text-slate-400">{toastMessage.sub}</p></div>
          </div>
        </div>
      )}

      {settings.isGstEnabled && <GSTR1Report initialRange={globalRange} />}
      <ExecutiveGrowthReport initialRange={globalRange} />
      <SubscriptionLifecycleReport initialRange={globalRange} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <MRRMovementChart initialRange={globalRange} />
        <NRRTrendChart initialRange={globalRange} />
        <CohortHeatmap initialRange={globalRange} />
        <UnitEconomicsCard initialRange={globalRange} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <TrialFunnel initialRange={globalRange} />
        <EngagementStickinessChart initialRange={globalRange} />
        <GeographicRevenueChart initialRange={globalRange} />
      </div>

      {isCreatingReport && <CreateReportModal onClose={() => setIsCreatingReport(false)} onSave={handleCreateReport} />}
    </div>
  );
};

export default Reports;
